import { Router } from '@angular/router';
import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { FireEntranceExitUser } from '../firestore-models/entrance-exit-user';
import { ApiEntranceExitUser } from '../api-client-module';
import { ApiSetEntranceExitUsersRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { EntranceExitUserDialogComponent } from './entrance-exit-user-dialog/entrance-exit-user-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { ConfigurationService } from 'src/app/configuration.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-entrance-exit-user',
  templateUrl: './entrance-exit-user.component.html',
  styleUrls: ['./entrance-exit-user.component.css'],
  standalone: false,
})
export class EntranceExitUserComponent implements OnInit, OnDestroy {

  private usersCollection!: AngularFirestoreCollection<FireEntranceExitUser>;
  usersObservable!: Observable<FireEntranceExitUser[]>;
  usersSubscribe!: Subscription;

  displayedColumns: string[] = ['position', 'edit', 'select', 'uid', 'email', 'name', 'desc'];
  dataSource = new MatTableDataSource<FireEntranceExitUser>();
  selection = new SelectionModel<FireEntranceExitUser>(true, []);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private clipboard: Clipboard,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.usersCollection = this.angularFirestore.collection<FireEntranceExitUser>(
      AppConst.FIRESTORE_ENTRANCE_EXIT_USERS, ref => ref.where('project_id', '==', this.configurationService.getProjectId()));
    this.usersObservable = this.usersCollection.valueChanges({ idField: 'id' });
    this.usersSubscribe = this.usersObservable.subscribe(users => {
      this.dataSource.data = users;
    });
  }

  ngOnDestroy(): void {
    this.usersSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: FireEntranceExitUser): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  openEditDialog(user: FireEntranceExitUser): void {
    let apiUser: ApiEntranceExitUser = {
      project_id: user.project_id,
      uid: user.uid,
      name: user.name,
      description: user.description,
      email: user.email,
      enabled: user.enabled,
    };
    const dialogRef = this.dialog.open(EntranceExitUserDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, user: apiUser }
    });
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(EntranceExitUserDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, user: null }
    });
  }

  copyClipboard(): void {
    const url = `${environment.qrBaseUrl}${this.configurationService.getProjectId()}`;
    this.clipboard.copy(url);
  }

  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedUsers: Array<ApiEntranceExitUser> = new Array<ApiEntranceExitUser>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedUser: ApiEntranceExitUser = {
            project_id: row.project_id,
            uid: row.uid,
            name: row.name,
            description: row.description,
            email: row.email,
            enabled: row.enabled,

          };
          selectedUsers.push(selectedUser);
        }
      }
      if (!!result) {
        let request: ApiSetEntranceExitUsersRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          users: selectedUsers
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setEntranceExitUsers(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'))
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'))
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
  
    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'uid':
          return compare(a.uid, b.uid, isAsc);
        case 'name':
          return compare(a.name, b.name, isAsc);
        case 'desc':
          return compare(a.description, b.description, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
